/**
 * @zendir/ui - SideNav Component
 *
 * Persistent sidebar navigation for operator dashboards. Compound component
 * pattern with Header, Item, Section, Divider, and Footer subcomponents.
 *
 * Responsive: Full sidebar on desktop, hamburger overlay on mobile.
 *
 * Astro UX Compliance:
 * - Persistent side navigation pattern (AstroUXDS Navigation)
 * - Status indicator integration
 * - Active state highlighting with accent color
 * - Keyboard navigation support
 * - Reduced motion support
 *
 * @example
 * ```tsx
 * <SideNav>
 *   <SideNav.Header logo={<Icon name="satellite" size={24} />} title="Space Range" badge="Operator" />
 *   <SideNav.Section title="Operations">
 *     <SideNav.Item icon="controls" label="Controls" description="System command interface" href="/controls" active />
 *     <SideNav.Item icon="telemetry" label="Telemetry" href="/telemetry" badge={3} tag="LIVE" tagVariant="success" />
 *     <SideNav.Item icon="images" label="Images" href="/images" />
 *   </SideNav.Section>
 *   <SideNav.Divider />
 *   <SideNav.Section title="Analysis">
 *     <SideNav.Item icon="chart" label="Plots" href="/plots" />
 *     <SideNav.Item icon="map" label="Map" href="/map" />
 *   </SideNav.Section>
 *   <SideNav.Footer>
 *     <SideNav.Item icon="settings" label="Settings" href="/settings" />
 *   </SideNav.Footer>
 * </SideNav>
 * ```
 */

import React, {
  memo,
  useState,
  createContext,
  useContext,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;
import { useTheme } from "../../theme";
import { safeAccentText } from "../../utils";
import { Icon } from "../display/Icon";
import type { IconName } from "../display/Icon";

// ─── Reveal animation ────────────────────────────────────────────────────────

/**
 * Content that only renders when expanded (item labels, section titles, the
 * header meta block) fades and slides in rather than popping, so widening the
 * rail reads as material flowing in. Injected once as a stylesheet because
 * keyframes can't be expressed inline; honours `prefers-reduced-motion`.
 */
const SIDENAV_REVEAL_STYLE_ID = "zendir-sidenav-reveal";
function ensureSideNavReveal(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(SIDENAV_REVEAL_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = SIDENAV_REVEAL_STYLE_ID;
  el.textContent =
    "@keyframes zsn-reveal{from{opacity:0;transform:translateX(-6px)}to{opacity:1;transform:translateX(0)}}" +
    ".zsn-reveal{animation:zsn-reveal 220ms cubic-bezier(0.22,1,0.36,1)}" +
    // Item label is always mounted and toggled via data-collapsed, so it fades
    // and slides BOTH on expand and collapse (the icon stays put as the anchor).
    ".zsn-label{transition:opacity 220ms cubic-bezier(0.22,1,0.36,1),transform 220ms cubic-bezier(0.22,1,0.36,1)}" +
    '.zsn-label[data-collapsed="true"]{opacity:0;transform:translateX(-6px);pointer-events:none}' +
    "@media (prefers-reduced-motion: reduce){.zsn-reveal,.zsn-label{animation:none;transition:none}}";
  document.head.appendChild(el);
}

// ─── Astro UX Status Shape ───────────────────────────────────────────────────

/** Astro UX dual-coded status shape (color + geometry). */
function NavStatusShape({
  status,
  size = 8,
}: {
  status: string;
  size?: number;
}) {
  const { tokens } = useTheme();
  const color =
    tokens.colors.status[status as keyof typeof tokens.colors.status] ??
    tokens.colors.status.off;
  const glow = `${color}50`;
  const style = {
    flexShrink: 0 as const,
    filter: `drop-shadow(0 0 3px ${glow})`,
  };

  switch (status) {
    case "caution":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={style}
          aria-hidden="true"
        >
          <rect x="1" y="1" width="10" height="10" fill={color} />
        </svg>
      );
    case "serious":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={style}
          aria-hidden="true"
        >
          <polygon points="6,1 11,6 6,11 1,6" fill={color} />
        </svg>
      );
    case "critical":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={style}
          aria-hidden="true"
        >
          <polygon points="6,11 1,2 11,2" fill={color} />
        </svg>
      );
    case "standby":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={style}
          aria-hidden="true"
        >
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        </svg>
      );
    case "off":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={style}
          aria-hidden="true"
        >
          <circle cx="6" cy="6" r="3" fill={color} />
        </svg>
      );
    default: // normal
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={style}
          aria-hidden="true"
        >
          <circle cx="6" cy="6" r="5" fill={color} />
        </svg>
      );
  }
}

// ─── Responsive Breakpoints ──────────────────────────────────────────────────

const SIDENAV_BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
} as const;

type SideNavMode = "desktop" | "tablet" | "mobile";

function getSideNavMode(width: number): SideNavMode {
  if (width < SIDENAV_BREAKPOINTS.mobile) return "mobile";
  if (width < SIDENAV_BREAKPOINTS.tablet) return "tablet";
  return "desktop";
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface SideNavContextValue {
  collapsed: boolean;
  mobileOpen: boolean;
  mode: SideNavMode;
  density: "default" | "compact";
  showCollapseToggle: boolean;
  setMobileOpen: (open: boolean) => void;
  toggleCollapse: () => void;
}

const SideNavContext = createContext<SideNavContextValue>({
  collapsed: false,
  mobileOpen: false,
  mode: "desktop",
  density: "default",
  showCollapseToggle: true,
  setMobileOpen: () => {},
  toggleCollapse: () => {},
});

// ─── SideNav ─────────────────────────────────────────────────────────────────

export interface SideNavProps {
  /** Collapsed mode (icon-only). When omitted, auto-collapses on tablet viewports. */
  collapsed?: boolean;
  /** Callback when collapsed state changes (via toggle button or responsive breakpoint). */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Show a collapse/expand toggle button in the sidebar (default true). */
  showCollapseToggle?: boolean;
  /** Width in pixels (default 260) */
  width?: number;
  /** Collapsed width in pixels (default 64) */
  collapsedWidth?: number;
  /**
   * Row density. `'compact'` tightens row padding, drops the left accent bar
   * in favour of a pill-style (rounded, bg-tinted) active row, and shrinks the
   * label — matching the v2.3 operator/admin secondary-nav mock. Default
   * `'default'`. (TODO: row inset + mono section headings for full mock parity.)
   */
  density?: "default" | "compact";
  /**
   * Mobile viewport behavior.
   * - `'drawer'` (default): hamburger button with slide-out overlay drawer.
   * - `'collapsed'`: persistent collapsed icon-only strip (same as tablet).
   */
  mobileVariant?: "drawer" | "collapsed";
  /** Children (SideNav.Header, SideNav.Item, SideNav.Section, SideNav.Footer) */
  children?: React.ReactNode;
  /** Custom style */
  style?: React.CSSProperties;
}

const SideNavRoot = memo(function SideNav({
  collapsed,
  onCollapsedChange,
  showCollapseToggle = true,
  width = 260,
  collapsedWidth = 64,
  density = "default",
  mobileVariant = "drawer",
  children,
  style,
}: SideNavProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState<boolean | null>(null);

  // Initialize to 'desktop' to guarantee the first client render matches SSR
  // (where window is undefined), preventing a React hydration mismatch if the
  // client happens to be on a mobile/tablet width at initial load.
  const [viewMode, setViewMode] = useState<SideNavMode>("desktop");

  useIsomorphicLayoutEffect(() => {
    if (typeof window === "undefined") return;

    // Synchronously correct the viewMode right after hydration but before paint
    setViewMode(getSideNavMode(window.innerWidth));

    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() =>
        setViewMode(getSideNavMode(window.innerWidth)),
      );
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    ensureSideNavReveal();
  }, []);

  // When mobileVariant='collapsed', promote mobile to tablet (collapsed inline strip)
  const effectiveMode =
    mobileVariant === "collapsed" && viewMode === "mobile"
      ? "tablet"
      : viewMode;

  // Reset user toggle when crossing breakpoints
  useEffect(() => {
    setUserCollapsed(null);
  }, [effectiveMode]);

  // Close mobile drawer when switching away from mobile
  useEffect(() => {
    if (effectiveMode !== "mobile" && mobileOpen) setMobileOpen(false);
  }, [effectiveMode, mobileOpen]);

  // Close mobile nav on escape
  useEffect(() => {
    if (!mobileOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [mobileOpen]);

  // Resolve collapsed: explicit prop > user toggle > auto (tablet = collapsed)
  const autoCollapsed = effectiveMode === "tablet";
  const isCollapsed =
    collapsed !== undefined
      ? collapsed
      : userCollapsed !== null
        ? userCollapsed
        : autoCollapsed;
  const isMobile = effectiveMode === "mobile";
  const navWidth = isCollapsed ? collapsedWidth : width;

  const handleToggleCollapse = useCallback(() => {
    const next = !isCollapsed;
    setUserCollapsed(next);
    onCollapsedChange?.(next);
  }, [isCollapsed, onCollapsedChange]);

  const navStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: isMobile ? 280 : navWidth,
    height: "100%",
    backgroundColor: isTransparentTheme
      ? "rgba(15, 12, 30, 0.7)"
      : tokens.colors.background.surface,
    borderRight: `1px solid ${tokens.colors.border.muted}`,
    transition:
      "width 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.25s ease",
    overflowX: "hidden",
    overflowY: "auto",
    flexShrink: 0,
    fontFamily: tokens.typography.fontFamily.primary,
    ...(isTransparentTheme
      ? {
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        }
      : {}),
    ...style,
  };

  const contextValue: SideNavContextValue = {
    collapsed: isCollapsed,
    mobileOpen,
    mode: effectiveMode,
    density,
    showCollapseToggle,
    setMobileOpen,
    toggleCollapse: handleToggleCollapse,
  };

  // Mobile: hamburger button + overlay drawer
  if (isMobile) {
    return (
      <SideNavContext.Provider value={contextValue}>
        {/* Hamburger button */}
        <button
          aria-label="Open navigation"
          onClick={() => setMobileOpen(true)}
          style={{
            position: "fixed",
            top: 6,
            left: 6,
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            border: "none",
            borderRadius: tokens.borderRadius.sm,
            backgroundColor: "transparent",
            color: tokens.colors.text.primary,
            cursor: "pointer",
            padding: 0,
            transition: tokens.animation.fast,
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Overlay */}
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1002,
            backgroundColor: `${tokens.colors.background.base}99`,
            backdropFilter: "blur(4px)",
            opacity: mobileOpen ? 1 : 0,
            pointerEvents: mobileOpen ? "auto" : "none",
            transition: "opacity 0.25s ease",
          }}
          onClick={() => setMobileOpen(false)}
        />

        {/* Slide-out nav */}
        <nav
          role="navigation"
          aria-label="Main navigation"
          style={{
            ...navStyle,
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 1003,
            transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
            boxShadow: mobileOpen ? tokens.shadows.xl : "none",
          }}
        >
          {children}
        </nav>
      </SideNavContext.Provider>
    );
  }

  return (
    <SideNavContext.Provider value={contextValue}>
      <nav role="navigation" aria-label="Main navigation" style={navStyle}>
        {children}
      </nav>
    </SideNavContext.Provider>
  );
});

// ─── Header ──────────────────────────────────────────────────────────────────

/** Default height for the logo row so dashboard / operator / other apps align the same mark. */
export const SIDENAV_HEADER_LOGO_SLOT_HEIGHT_PX = 44;

export interface SideNavHeaderProps {
  /** Logo element (Icon, image, or ReactNode) */
  logo?: React.ReactNode;
  /** Compact logo shown when sidebar is collapsed or on tablet (e.g., just the icon mark) */
  collapsedLogo?: React.ReactNode;
  /** App title */
  title?: string;
  /** Subtitle or version */
  subtitle?: string;
  /** Role badge (e.g., "Operator", "Admin") */
  badge?: string;
  /** Badge variant for color */
  badgeVariant?: "info" | "success" | "warning" | "caution";
  /**
   * Fixed height (px) for the top logo band. Title, subtitle, and badge render below this band
   * so different logo assets still line up across apps (dashboard vs operator).
   */
  logoSlotHeight?: number;
  /** Children override (advanced: replaces default header content entirely) */
  children?: React.ReactNode;
}

/** Chevron toggle button for collapsing/expanding the sidebar. */
function CollapseToggleButton() {
  const { tokens } = useTheme();
  const { collapsed, toggleCollapse } = useContext(SideNavContext);
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      onClick={toggleCollapse}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        border: `1px solid ${hovered ? tokens.colors.border.focus : tokens.colors.border.muted}`,
        borderRadius: tokens.borderRadius.md,
        backgroundColor: hovered
          ? `${tokens.colors.accent.primary}15`
          : "transparent",
        color: hovered
          ? tokens.colors.accent.primary
          : tokens.colors.text.tertiary,
        cursor: "pointer",
        flexShrink: 0,
        padding: 0,
        transition: tokens.animation.fast,
        outline: "none",
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transition: "transform 0.25s ease",
          transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
        }}
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  );
}

const SideNavHeader = memo(function SideNavHeader({
  logo,
  collapsedLogo,
  title,
  subtitle,
  badge,
  badgeVariant = "info",
  logoSlotHeight = SIDENAV_HEADER_LOGO_SLOT_HEIGHT_PX,
  children,
}: SideNavHeaderProps): React.ReactElement {
  const { tokens } = useTheme();
  const { collapsed, mode, showCollapseToggle, toggleCollapse } =
    useContext(SideNavContext);
  const displayLogo = collapsed && collapsedLogo ? collapsedLogo : logo;
  const showToggle = showCollapseToggle && mode !== "mobile";
  const hasMeta = Boolean(title || subtitle || badge);
  const slotH = logoSlotHeight;

  const badgeColors: Record<string, string> = {
    info: tokens.colors.accent.primary,
    success: tokens.colors.status.normal,
    warning: tokens.colors.status.caution,
    caution: tokens.colors.status.serious,
  };

  const metaBlock =
    !collapsed && hasMeta ? (
      <div
        className="zsn-reveal"
        style={{ flex: 1, minWidth: 0, width: "100%" }}
      >
        {title ? (
          <div
            style={{
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: tokens.typography.fontWeight.bold,
              color: tokens.colors.text.primary,
              lineHeight: tokens.typography.lineHeight.tight,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </div>
        ) : null}
        {subtitle || badge ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.xs,
              marginTop: title ? "2px" : 0,
              flexWrap: "wrap",
            }}
          >
            {subtitle ? (
              <span
                style={{
                  fontSize: tokens.typography.fontSize.xxs,
                  color: tokens.colors.text.tertiary,
                }}
              >
                {subtitle}
              </span>
            ) : null}
            {badge ? (
              <span
                style={{
                  fontSize: "0.6rem",
                  fontWeight: tokens.typography.fontWeight.bold,
                  color:
                    badgeColors[badgeVariant] ||
                    safeAccentText(tokens.colors.accent.primary),
                  backgroundColor: `${badgeColors[badgeVariant] || tokens.colors.accent.primary}18`,
                  border: `1px solid ${badgeColors[badgeVariant] || tokens.colors.accent.primary}30`,
                  padding: "1px 6px",
                  borderRadius: tokens.borderRadius.sm,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {badge}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    ) : null;

  const logoSlot = displayLogo ? (
    <div
      style={{
        height: slotH,
        minHeight: slotH,
        maxHeight: slotH,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        overflow: "hidden",
        boxSizing: "border-box",
        marginBottom:
          !collapsed && hasMeta && displayLogo ? tokens.spacing.sm : 0,
      }}
    >
      <div
        style={{
          maxHeight: "100%",
          maxWidth: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          minWidth: 0,
          cursor: collapsed && showToggle ? "pointer" : undefined,
        }}
        onClick={collapsed && showToggle ? toggleCollapse : undefined}
      >
        {displayLogo}
      </div>
    </div>
  ) : null;

  if (children) {
    return (
      <div
        style={{
          padding: `${tokens.spacing.md} 16px ${tokens.spacing.md} 15px`,
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
          flexShrink: 0,
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        {children}
      </div>
    );
  }

  // Collapsed: centered logo in the same fixed slot height as expanded (visual parity with operator).
  if (collapsed) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: `${tokens.spacing.md} ${tokens.spacing.sm}`,
          minHeight: slotH + 24,
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
          flexShrink: 0,
          boxSizing: "border-box",
          position: "relative",
          justifyContent: "center",
        }}
      >
        {logoSlot}
        {showToggle ? (
          <div
            style={{
              position: "absolute",
              right: 4,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <CollapseToggleButton />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        padding: `${tokens.spacing.md} 16px ${tokens.spacing.md} 15px`,
        borderBottom: `1px solid ${tokens.colors.border.muted}`,
        flexShrink: 0,
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      {showToggle ? (
        <div
          style={{
            position: "absolute",
            top: tokens.spacing.sm,
            right: tokens.spacing.sm,
            zIndex: 1,
          }}
        >
          <CollapseToggleButton />
        </div>
      ) : null}
      <div
        style={{
          paddingRight: showToggle ? 36 : 0,
          width: "100%",
          minWidth: 0,
        }}
      >
        {logoSlot}
        {metaBlock}
      </div>
    </div>
  );
});

// ─── Item ────────────────────────────────────────────────────────────────────

export interface SideNavItemProps {
  /** Icon name from zendir-ui icon library */
  icon?: IconName | React.ReactNode;
  /** Label text */
  label: string;
  /** Description text displayed beneath the label (hidden in collapsed mode) */
  description?: string;
  /** Small tag/chip displayed after the label (e.g. "v2", "NEW", "BETA") */
  tag?: string;
  /** Tag color variant */
  tagVariant?: "default" | "info" | "success" | "warning" | "danger";
  /** Link href (renders <a>) */
  href?: string;
  /** Click handler (renders <button>) */
  onClick?: () => void;
  /** Active state */
  active?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Notification badge count */
  badge?: number;
  /** External link indicator */
  external?: boolean;
  /**
   * Astro UX status level — renders a dual-coded indicator (color + shape).
   * Shapes per official Astro UXDS: normal = ● filled circle, standby = ◎ ring,
   * caution = ■ square, serious = ◆ diamond, critical = ▼ triangle, off = · small circle.
   */
  status?: "off" | "standby" | "normal" | "caution" | "serious" | "critical";
  /** Optional status label shown as tooltip or text next to status shape */
  statusLabel?: string;
  /** Right-side slot — arbitrary ReactNode rendered at the end of the item row */
  suffix?: React.ReactNode;
}

const TAG_COLORS: Record<string, { bg: string; fg: string; border: string }> = {
  default: { bg: "#ffffff10", fg: "#9590a8", border: "#ffffff15" },
  info: { bg: "#8a2be218", fg: "#c4a0ff", border: "#8a2be230" },
  success: { bg: "#56f00018", fg: "#56f000", border: "#56f00030" },
  warning: { bg: "#fce83a18", fg: "#fce83a", border: "#fce83a30" },
  danger: { bg: "#ff383818", fg: "#ff3838", border: "#ff383830" },
};

const SideNavItem = memo(function SideNavItem({
  icon,
  label,
  description,
  tag,
  tagVariant = "default",
  href,
  onClick,
  active = false,
  disabled = false,
  badge,
  external = false,
  status,
  statusLabel,
  suffix,
}: SideNavItemProps): React.ReactElement {
  const { tokens } = useTheme();
  const { collapsed, setMobileOpen, density } = useContext(SideNavContext);
  const compact = density === "compact" && !collapsed;
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      if (onClick && href && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
      }
      setMobileOpen(false);
      onClick?.();
    },
    [disabled, onClick, href, setMobileOpen],
  );

  // Status-aware accent: when an item has a status, tint active/hover with its color
  const statusColor = status
    ? (tokens.colors.status[status] ?? undefined)
    : undefined;
  const accentColor =
    statusColor && active
      ? statusColor
      : safeAccentText(tokens.colors.accent.primary);

  const hasDescription = !!description && !collapsed;

  const iconElement =
    typeof icon === "string" ? (
      <Icon
        name={icon as IconName}
        size={hasDescription ? 22 : 20}
        color={active ? accentColor : tokens.colors.text.secondary}
      />
    ) : (
      icon
    );

  const itemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: hasDescription ? "flex-start" : "center",
    gap: tokens.spacing.sm,
    // Collapsed keeps the SAME 12px left inset as the expanded row, so the
    // icon holds its horizontal position when the rail expands — it doesn't
    // jump from a centered slot to a left-aligned one. Size the collapsed
    // width so that fixed inset reads as centered.
    padding: collapsed
      ? `${tokens.spacing.sm} 12px`
      : compact
        ? "7px 8px"
        : hasDescription
          ? `10px 16px 10px 12px`
          : `${tokens.spacing.sm} 16px ${tokens.spacing.sm} 12px`,
    // Compact: pill-style rounded row (mock parity). Default: square row.
    borderRadius: compact ? tokens.borderRadius.md : 0,
    justifyContent: "flex-start",
    color: active
      ? accentColor
      : disabled
        ? tokens.colors.text.tertiary
        : tokens.colors.text.secondary,
    backgroundColor: active
      ? `${accentColor}12`
      : isHovered && !disabled
        ? `${accentColor}08`
        : "transparent",
    fontSize: compact
      ? tokens.typography.fontSize.xs
      : tokens.typography.fontSize.sm,
    fontWeight: active
      ? tokens.typography.fontWeight.semibold
      : tokens.typography.fontWeight.normal,
    fontFamily: tokens.typography.fontFamily.primary,
    cursor: disabled ? "not-allowed" : "pointer",
    textDecoration: "none",
    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    // Compact drops the left accent bar — the active row is conveyed by the
    // bg tint + accent text alone (mock parity). Default keeps the 3px bar.
    borderLeft: compact
      ? "none"
      : active
        ? `3px solid ${accentColor}`
        : "3px solid transparent",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    transition: tokens.animation.fast,
    position: "relative",
    opacity: disabled ? 0.5 : 1,
  };

  const tagColors = TAG_COLORS[tagVariant] ?? TAG_COLORS.default;

  const content = (
    <>
      {/* Icon + collapsed status overlay */}
      {iconElement && (
        <span
          style={{
            flexShrink: 0,
            display: "flex",
            position: "relative",
            marginTop: hasDescription ? "2px" : 0,
          }}
        >
          {iconElement}
          {/* In collapsed mode, show a small status shape overlaid on the icon (top-left) */}
          {collapsed && status && (
            <span
              style={{
                position: "absolute",
                top: -3,
                left: -4,
                lineHeight: 0,
              }}
              title={statusLabel ?? status}
            >
              <NavStatusShape status={status} size={7} />
            </span>
          )}
        </span>
      )}
      {/* Label — always mounted and toggled via data-collapsed so it fades and
          slides BOTH on expand and collapse; the icon holds its position as
          the anchor and only the label moves. */}
      <span
        className="zsn-label"
        data-collapsed={collapsed ? "true" : "false"}
        style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </span>
          {tag && (
            <span
              style={{
                fontSize: "0.6rem",
                fontWeight: tokens.typography.fontWeight.bold,
                color: tagColors.fg,
                backgroundColor: tagColors.bg,
                border: `1px solid ${tagColors.border}`,
                padding: "1px 5px",
                borderRadius: tokens.borderRadius.sm,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                flexShrink: 0,
                lineHeight: "1.3",
                whiteSpace: "nowrap",
              }}
            >
              {tag}
            </span>
          )}
        </span>
        {description && !collapsed && (
          <span
            style={{
              display: "block",
              fontSize: tokens.typography.fontSize.xxs,
              color: tokens.colors.text.tertiary,
              lineHeight: tokens.typography.lineHeight.normal,
              marginTop: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: tokens.typography.fontWeight.normal,
            }}
          >
            {description}
          </span>
        )}
      </span>
      {/* Right-side trailing elements — expanded only; collapsed shows the
          status overlay on the icon instead. */}
      {!collapsed &&
        ((badge !== undefined && badge > 0) ||
          status ||
          suffix ||
          external) && (
          <span
            className="zsn-reveal"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
              marginTop: hasDescription ? "2px" : 0,
            }}
          >
            {/* Badge */}
            {badge !== undefined && badge > 0 && (
              <span
                style={{
                  fontSize: "0.65rem",
                  fontWeight: tokens.typography.fontWeight.bold,
                  color: "#fff",
                  backgroundColor: tokens.colors.status.critical,
                  borderRadius: tokens.borderRadius.full,
                  minWidth: "18px",
                  height: "18px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 5px",
                  flexShrink: 0,
                  lineHeight: 1,
                  boxSizing: "border-box",
                }}
              >
                {badge > 99 ? "99+" : badge}
              </span>
            )}
            {/* Status */}
            {status && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
                role="status"
                aria-label={`Status: ${statusLabel ?? status}`}
                title={statusLabel ?? status}
              >
                <NavStatusShape status={status} size={8} />
              </span>
            )}
            {/* Suffix */}
            {suffix && (
              <span
                style={{
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                {suffix}
              </span>
            )}
            {/* External */}
            {external && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                style={{ flexShrink: 0, opacity: 0.5 }}
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            )}
          </span>
        )}
    </>
  );

  const props = {
    style: itemStyle,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    title: collapsed ? label : undefined,
    "aria-current": active ? ("page" as const) : undefined,
    "aria-disabled": disabled,
  };

  if (href && !disabled) {
    return (
      <a
        href={href}
        onClick={handleClick}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={handleClick} disabled={disabled} {...props}>
      {content}
    </button>
  );
});

// ─── Section ─────────────────────────────────────────────────────────────────

export interface SideNavSectionProps {
  /** Section title */
  title?: string;
  /** Children (SideNav.Item elements) */
  children?: React.ReactNode;
}

const SideNavSection = memo(function SideNavSection({
  title,
  children,
}: SideNavSectionProps): React.ReactElement {
  const { tokens } = useTheme();
  const { collapsed } = useContext(SideNavContext);

  return (
    <div style={{ marginTop: tokens.spacing.sm }}>
      {title && !collapsed && (
        <div
          className="zsn-reveal"
          style={{
            padding: `${tokens.spacing.xs} 16px ${tokens.spacing.xs} 15px`,
            fontSize: tokens.typography.fontSize.xxs,
            fontWeight: tokens.typography.fontWeight.bold,
            color: tokens.colors.text.tertiary,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          {title}
        </div>
      )}
      {collapsed && title && (
        <div
          style={{
            width: "60%",
            height: "1px",
            backgroundColor: tokens.colors.border.muted,
            margin: `${tokens.spacing.xs} auto`,
          }}
        />
      )}
      {children}
    </div>
  );
});

// ─── Divider ─────────────────────────────────────────────────────────────────

export interface SideNavDividerProps {
  /** Optional label shown in the center of the divider line */
  label?: string;
}

const SideNavDivider = memo(function SideNavDivider({
  label,
}: SideNavDividerProps): React.ReactElement {
  const { tokens } = useTheme();
  const { collapsed } = useContext(SideNavContext);

  if (collapsed) {
    return (
      <div
        style={{
          width: "60%",
          height: "1px",
          backgroundColor: tokens.colors.border.muted,
          margin: `${tokens.spacing.sm} auto`,
        }}
      />
    );
  }

  if (label) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.spacing.sm,
          padding: `${tokens.spacing.sm} 16px ${tokens.spacing.sm} 15px`,
        }}
      >
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: tokens.colors.border.muted,
          }}
        />
        <span
          style={{
            fontSize: tokens.typography.fontSize.xxs,
            color: tokens.colors.text.tertiary,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: tokens.colors.border.muted,
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        height: "1px",
        backgroundColor: tokens.colors.border.muted,
        margin: `${tokens.spacing.sm} 16px ${tokens.spacing.sm} 15px`,
      }}
    />
  );
});

// ─── Footer ──────────────────────────────────────────────────────────────────

export interface SideNavFooterProps {
  children?: React.ReactNode;
}

const SideNavFooter = memo(function SideNavFooter({
  children,
}: SideNavFooterProps): React.ReactElement {
  const { tokens } = useTheme();

  return (
    <div
      style={{
        marginTop: "auto",
        borderTop: `1px solid ${tokens.colors.border.muted}`,
        paddingTop: tokens.spacing.xs,
        paddingBottom: tokens.spacing.xs,
      }}
    >
      {children}
    </div>
  );
});

// ─── Compound Export ─────────────────────────────────────────────────────────

type SideNavComponent = typeof SideNavRoot & {
  Header: typeof SideNavHeader;
  Item: typeof SideNavItem;
  Section: typeof SideNavSection;
  Divider: typeof SideNavDivider;
  Footer: typeof SideNavFooter;
};

export const SideNav: SideNavComponent = Object.assign(SideNavRoot, {
  Header: SideNavHeader,
  Item: SideNavItem,
  Section: SideNavSection,
  Divider: SideNavDivider,
  Footer: SideNavFooter,
});

export default SideNav;
